import { Component, EventEmitter, Input, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { IClient } from '../../../../../core/models/clients.models';
import { ICampaign } from '../../../../../core/models/campaigns.model';
import { CampaignsApiService } from '../../../../campaigns/campaigns.apiservice';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { IProductFinalized } from '../../../../../core/models/products.models';
import { PRODUCT_FAMILY } from '../../../../../core/helpers/constants';

@Component({
  selector: 'app-campaign-selection',
  templateUrl: './campaign-selection.component.html',
  styleUrls: ['./campaign-selection.component.scss']
})
export class CampaignSelectionComponent implements OnDestroy, OnChanges {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Input() userRoles: Array<number> = [];
  @Input() finalizedProducts: Array<IProductFinalized> = [];
  @Input() addingConsulting: boolean = false;
  @Input() addingNational: boolean = false;
  @Input() addingFacebook: boolean = false;
  @Input() addingGoogle: boolean = false; 
  @Input() clientsData = new Array<IClient>();
  @Input() selectedClient: IClient = {} as IClient;

  tempClients = new Array<IClient>();
  campaignNames = new Map<string, Array<Map<string, string>>>();
  filteredCampaigns = new Map<string, Array<ICampaign>>();
  clientFilter: IClient = {} as IClient;

  @Output() clientChange = new EventEmitter<IClient>(); 
  @Output() campaignSelected = new EventEmitter<{ clientId: string; campaign: Map<string, string>; campaignId: string }>();
  @Output() newCampaign = new EventEmitter<boolean>();

  constructor(
    private campaignApiService: CampaignsApiService
  ) {
    
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedClient'] && this.selectedClient && this.selectedClient.id) {
      this.clientFilter = this.selectedClient;
      // If campaign lists are empty, reload campaigns for the selected client
      if (
        (!this.filteredCampaigns || this.filteredCampaigns.size === 0) &&
        (!this.campaignNames || this.campaignNames.size === 0)
      ) {
        this.filteredCampaigns = new Map<string, Array<ICampaign>>();
        this.campaignNames = new Map<string, Array<Map<string,string>>>();
        this.tempClients = [this.selectedClient];
        this.getCampaignsByClient(this.selectedClient.id);
      }
    } else if (changes['selectedClient'] && (!this.selectedClient || !this.selectedClient.id)) {
      this.clientFilter = {} as IClient;
    }
    if (changes['finalizedProducts']) {
      this.getAllCampaigns();
    }
  }

  getAllCampaigns() {
    if (this.finalizedProducts.filter(x => x.familyCode !== PRODUCT_FAMILY.AdSpend).length > 0) {
      if (this.selectedClient && this.selectedClient.id) {
        this.tempClients = [this.selectedClient];
        this.clientFilter = this.selectedClient;
      } else {
        this.tempClients = this.clientsData;
      }

      if (!this.userRoles.includes(1) && !this.userRoles.includes(4)) {
        // this.checkoutClients = this.clientsData;
        this.tempClients.forEach((x: IClient) => {
          this.getCampaignsByClient(x.id);
        });
      }
    }
  }

  onClientChange(event: IClient) {
    this.clientFilter = event;
    if (event.fullName) {
      this.filteredCampaigns = new Map<string, Array<ICampaign>>();
      this.campaignNames = new Map<string, Array<Map<string,string>>>();
      this.tempClients = this.clientsData.filter(x => x.fullName.toLowerCase().includes(event.fullName.toLowerCase()));
      this.tempClients.forEach((x: IClient) => {
        this.getCampaignsByClient(x.id);
      });
    } 

    // this.selectedClient = event;
    this.clientChange.emit(event);
  }

  onClientChangeById(clientId: string, clientSelect: any) {
    const selected = this.clientsData.find(x => x.id === clientId);
    if (selected) {
      this.clientFilter = selected;
      this.clientChange.emit(selected);
      this.filteredCampaigns = new Map<string, Array<ICampaign>>();
      this.campaignNames = new Map<string, Array<Map<string,string>>>();
      this.tempClients = [selected];
      this.getCampaignsByClient(selected.id);
    }
    // Blur the select after selection
    if (clientSelect && clientSelect.focused) {
      setTimeout(() => clientSelect.blur(), 0);
    }
  }

  onClientClear(clientSelect: any) {
    this.clientFilter = {} as IClient;
    this.tempClients = this.clientsData;
    this.filteredCampaigns = new Map<string, Array<ICampaign>>();
    this.campaignNames = new Map<string, Array<Map<string,string>>>();
    if (!this.userRoles.includes(1) && !this.userRoles.includes(4)) {
      this.clientsData.forEach((x: IClient) => {
        this.getCampaignsByClient(x.id);
      });
    }
    this.clientChange.emit(undefined);
    if (clientSelect && clientSelect.focused) {
      setTimeout(() => clientSelect.blur(), 0);
    }
  }

  getCampaignsByClient(clientId: string) {
    // this.campForm.patchValue({ campaignId: null });
    this.campaignApiService.getCampaignsByClient(clientId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res: any) => {
        if (res.good) {
          const updatedCampaigns = res.data.map((x: any) => {
            x.disabled = x.rmSubscriptionId != null && x.rmSubscriptionId.length > 0;
            x.subscriptionId = x.rmSubscriptionId;
            return x;
          });
          updatedCampaigns.forEach((x: ICampaign) => {
            if (x.platform === 'Facebook') {
              var name = x.name.substring(0, x.name.indexOf(' FB'));
            }
            else {
              var name = x.name;
            }
            if (this.filteredCampaigns.get(name) === undefined) {
              this.filteredCampaigns.set(name, []); // Initialize an array for this campaign name if it doesn't exist
            }
            const group = this.filteredCampaigns.get(name); // Add the current campaign to its respective group
            group?.push(x);
            if (this.campaignNames.get(clientId) === undefined) {
              this.campaignNames.set(clientId, new Array<Map<string,string>>());
            }
            // checkDup is now the array of Maps of string
            var checkDup = this.campaignNames.get(clientId);
            if (checkDup!.find(x => x.get('name') === name) === undefined) {
              this.campaignNames.get(clientId)?.push(new Map([["name", name], ["platform", x.platform]]));
              this.campaignNames.get(clientId)?.sort((a, b) => a.get('name')!.localeCompare(b.get('name')!));
            }

            // if (checkDup!.includes(name) === false) {
            //   this.campaignNames.get(clientId)?.push(name);
            //   this.campaignNames.get(clientId)?.sort((a, b) => a!.localeCompare(b!));;
            // }
          });
        }
      });
  }

  selectedCampaign(clientId: string, campaign: Map<string, string>) {
    const selectedCampaign = this.filteredCampaigns.get(campaign.get('name')!);
    if (!selectedCampaign || selectedCampaign.length === 0) {
      console.error("No campaigns found for the selected name.");
      return;
    }
    const campaignId = selectedCampaign[0]?.id;

    if (this.checkCampPlatforms(campaign)) {
      this.campaignSelected.emit({ clientId, campaign, campaignId });
    }
  }

  addNewCampaign(event: boolean) {
    this.newCampaign.emit(event);
  }

  checkCampPlatforms(campaign: Map<string, string>): boolean {
    const platform = campaign.get('platform')?.toLowerCase() || '';
    const campaigns = this.filteredCampaigns.get(campaign.get('name')!) || [];

    if (this.addingNational) {
      if (platform.includes('national')) {
        return !campaigns.some(x => x.subscriptionActive);
      }
    } else if (this.addingConsulting) {
      if (platform.includes('consulting')) {
        return !campaigns.some(x => x.subscriptionActive);
      }
    } else if (this.addingFacebook || this.addingGoogle) {
      if (platform.includes('google') || platform.includes('facebook')) {
        return !campaigns.some(x => x.subscriptionActive);
      }
    } 
    
    return false;
  }

  get selectableCampaigns() {
    const result: Array<{ client: IClient, campaign: Map<string, string> }> = [];
    for (const client of this.clientsData) {
      const campaigns = this.campaignNames.get(client.id) || [];
      for (const campaign of campaigns) {
        if (this.checkCampPlatforms(campaign) && campaign.get('name')) {
          result.push({ client, campaign });
        }
      }
    }
    return result;
  }

  get inactiveCampaigns() {
    const result: Array<{ client: IClient, campaign: Map<string, string> }> = [];
    for (const client of this.clientsData) {
      const campaigns = this.campaignNames.get(client.id) || [];
      for (const campaign of campaigns) {
        if (!this.checkCampPlatforms(campaign) && campaign.get('name')) {
          result.push({ client, campaign });
        }
      }
    }
    return result;
  }
}